import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  coursesService,
  CourseMutationPayload,
  CourseModuleMutationPayload,
  CourseQueryParams,
  CourseResourceLinkPayload,
  CourseResourceUploadPayload,
} from '@/services/coursesService'
import { CourseModel, CourseModuleModel, CourseResourceModel } from '@/models/course.model'
import { PaginatedResponse } from '@/models/templates/pagination.model'

export const courseKeys = {
  all: ['courses'] as const,
  lists: () => [...courseKeys.all, 'lists'] as const,
  list: (filters: CourseQueryParams) => [...courseKeys.lists(), filters] as const,
  details: () => [...courseKeys.all, 'detail'] as const,
  detail: (courseId: string) => [...courseKeys.details(), courseId] as const,
  modules: (courseId: string) => [...courseKeys.detail(courseId), 'modules'] as const,
  resources: (courseId: string) => [...courseKeys.detail(courseId), 'resources'] as const,
  moduleResources: (courseId: string, moduleId: string) =>
    [...courseKeys.resources(courseId), 'module', moduleId] as const,
  many: (courseIds: string[]) => [...courseKeys.all, 'many', [...courseIds].sort()] as const,
}

const useNormalizedFilters = (filters: CourseQueryParams = {}) =>
  useMemo(() => {
    const normalized: CourseQueryParams = {}
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return
      normalized[key as keyof CourseQueryParams] = value as never
    })
    return normalized
  }, [JSON.stringify(filters)])

export function useCourses(filters: CourseQueryParams = {}) {
  const normalizedFilters = useNormalizedFilters(filters)

  return useQuery({
    queryKey: courseKeys.list(normalizedFilters),
    queryFn: async (): Promise<PaginatedResponse<CourseModel>> => {
      return coursesService.getCoursesPage(normalizedFilters)
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export function useCourse(courseId: string | undefined) {
  return useQuery({
    queryKey: courseId ? courseKeys.detail(courseId) : courseKeys.details(),
    queryFn: async () => {
      if (!courseId) throw new Error('Course ID is required')
      return coursesService.getCourse(courseId)
    },
    enabled: Boolean(courseId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useCoursesByIds(courseIds: string[] | undefined) {
  const normalizedIds = useMemo(() => {
    if (!courseIds || courseIds.length === 0) return [] as string[]
    return Array.from(new Set(courseIds.filter(Boolean)))
  }, [courseIds?.join(',')])

  return useQuery({
    queryKey: courseKeys.many(normalizedIds),
    queryFn: async (): Promise<Record<string, CourseModel>> => {
      if (normalizedIds.length === 0) return {}
      const courses = await Promise.all(normalizedIds.map((courseId) => coursesService.getCourse(courseId)))
      return courses.reduce<Record<string, CourseModel>>((acc, course) => {
        if (course.id) {
          acc[course.id] = course
        }
        return acc
      }, {})
    },
    enabled: normalizedIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useCourseModules(courseId: string | undefined) {
  return useQuery({
    queryKey: courseId ? courseKeys.modules(courseId) : courseKeys.details(),
    queryFn: async () => {
      if (!courseId) throw new Error('Course ID is required')
      return coursesService.getModules(courseId)
    },
    enabled: Boolean(courseId),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useCourseModuleResources(courseId: string | undefined, moduleId: string | undefined) {
  return useQuery({
    queryKey: courseId && moduleId ? courseKeys.moduleResources(courseId, moduleId) : ['courses', 'resources', 'module'],
    queryFn: async () => {
      if (!courseId || !moduleId) throw new Error('Course ID and Module ID are required')
      return coursesService.getResources(courseId, { moduleId })
    },
    enabled: Boolean(courseId && moduleId),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useCreateCourse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CourseMutationPayload) => coursesService.createCourse(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.all })
    },
  })
}

export function useUpdateCourse(courseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CourseMutationPayload) => coursesService.updateCourse(courseId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) })
      queryClient.invalidateQueries({ queryKey: courseKeys.all })
    },
  })
}

export function useDeleteCourse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (courseId: string) => coursesService.deleteCourse(courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.all })
    },
  })
}

export function useCreateModule(courseId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CourseModuleMutationPayload) => {
      if (!courseId) throw new Error('Course ID is required')
      return coursesService.createModule(courseId, payload)
    },
    onSuccess: () => {
      if (!courseId) return
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(courseId) })
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) })
    },
  })
}

export function useUpdateModule(courseId: string | undefined, moduleId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CourseModuleMutationPayload) => {
      if (!courseId || !moduleId) throw new Error('Course ID and Module ID are required')
      return coursesService.updateModule(courseId, moduleId, payload)
    },
    onSuccess: () => {
      if (!courseId) return
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(courseId) })
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) })
    },
  })
}

export function useDeleteModule(courseId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (moduleId: string) => {
      if (!courseId) throw new Error('Course ID is required')
      return coursesService.deleteModule(courseId, moduleId)
    },
    onSuccess: () => {
      if (!courseId) return
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(courseId) })
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) })
    },
  })
}

export function useUploadCourseResources(courseId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CourseResourceUploadPayload) => {
      if (!courseId) throw new Error('Course ID is required')
      return coursesService.uploadResources(courseId, payload)
    },

    onSuccess: (_, variables) => {
      if (!courseId) return
      queryClient.invalidateQueries({ queryKey: courseKeys.resources(courseId) })
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) })
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(courseId) })
      if (variables?.moduleId) {
        queryClient.invalidateQueries({ queryKey: courseKeys.moduleResources(courseId, variables.moduleId) })
      }
    },
  })
}

export function useLinkCourseResource(courseId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CourseResourceLinkPayload) => {
      if (!courseId) throw new Error('Course ID is required')
      return coursesService.linkResource(courseId, payload)
    },
    onSuccess: (_, variables) => {
      if (!courseId) return
      queryClient.invalidateQueries({ queryKey: courseKeys.resources(courseId) })
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) })
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(courseId) })
      if (variables?.moduleId) {
        queryClient.invalidateQueries({ queryKey: courseKeys.moduleResources(courseId, variables.moduleId) })
      }
    },
  })
}

export function useDeleteCourseResource(courseId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (resourceId: string) => {
      if (!courseId) throw new Error('Course ID is required')
      return coursesService.deleteResource(courseId, resourceId)
    },
    onSuccess: () => {
      if (!courseId) return
      queryClient.invalidateQueries({ queryKey: courseKeys.resources(courseId) })
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) })
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(courseId) })
    },
  })
}

/**
 * Reorders modules by issuing sequential update requests with the new position.
 */
export function useReorderModules(courseId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      modules: Array<{ id: string; title: string; description?: string; isActive?: boolean; order: number }>,
    ) => {
      if (!courseId) throw new Error('Course ID is required')
      for (const module of modules) {
        await coursesService.updateModule(courseId, module.id, {
          title: module.title,
          description: module.description ?? undefined,
          order: module.order,
          is_active: typeof module.isActive === 'boolean' ? module.isActive : undefined,
        })
      }
    },
    onSuccess: () => {
      if (!courseId) return
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(courseId) })
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) })
    },
  })
}

/**
 * Placeholder hook for course progress metrics. Replace once the API endpoint becomes available.
 */
export function useCourseProgress(courseId: string | undefined) {
  return useQuery({
    queryKey: courseId ? [...courseKeys.detail(courseId), 'progress'] : courseKeys.details(),
    queryFn: async () => {
      if (!courseId) throw new Error('Course ID is required')
      return { courseId, overallProgress: 0 }
    },
    enabled: Boolean(courseId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}
